import Button from '@orn/shared-web/components/Button';
import { cx } from '@orn/shared-web/utils/cx';
import * as React from 'react';

import RecordHero, { RecordHeroTopRow } from './RecordHero';
import RecordRow from './RecordRow';
import RecordSection from './RecordSection';

export type RecordPaneStatus = 'loading' | 'error' | 'not-found' | 'ready';

interface IRecordPaneProps {
  status: RecordPaneStatus;
  /** The `RecordPane.Hero` for the record. Rendered only when `status` is `ready`. */
  hero?: React.ReactNode;
  /** Muted centered line at the very bottom (the raw description). */
  footer?: React.ReactNode;
  /** Closes the page — the hero's X, and the "Go back" action of the error states. */
  onClose: () => void;
  /** @default "This record isn't available." */
  notFoundMessage?: string;
  /** @default "Something went wrong loading this record." */
  errorMessage?: string;
  /** The body's sections, stacked with a 20px gap. */
  children?: React.ReactNode;
  className?: string;
}

/**
 * The page shell every record page (transaction, recurring stream, bill) is
 * built on: a dark hero on top and a light body whose rounded top overlaps
 * the hero's bottom edge by 20px, then stacked `RecordPane.Section` blocks
 * and a footer line.
 *
 * `status` picks between the record itself and three stand-ins that keep the
 * close affordance reachable: a skeleton while loading, and a message with a
 * "Go back" button for `error` and `not-found`.
 *
 * Sizing mirrors `EntityPane` — `min-h-full` + `shrink-0` so the pane fills
 * the card's scroller when short and grows past it when long.
 */
const RecordPane = ({
  status,
  hero,
  footer,
  onClose,
  notFoundMessage = "This record isn't available.",
  errorMessage = 'Something went wrong loading this record.',
  children,
  className,
}: IRecordPaneProps) => {
  return (
    <div className={cx('flex min-h-full w-full shrink-0 flex-col', className)}>
      {status === 'ready' ? hero : <RecordHeroStandIn status={status} onClose={onClose} />}
      <div className="bg-app_light relative -mt-5 flex w-full flex-1 flex-col gap-5 rounded-t-3xl px-5 pt-5">
        {status === 'ready' && children}
        {status === 'loading' && <RecordBodySkeleton />}
        {(status === 'error' || status === 'not-found') && (
          <div className="flex flex-col items-center gap-4 px-6 pb-5 pt-8 text-center">
            <p className="text-secondary text-md">{status === 'error' ? errorMessage : notFoundMessage}</p>
            <Button color="secondary" size="md" onClick={onClose}>
              Go back
            </Button>
          </div>
        )}
        {status === 'ready' && footer && (
          <div className="text-tertiary flex items-center justify-center px-6 pb-5 pt-8 text-center text-sm">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

interface IRecordHeroStandInProps {
  status: Exclude<RecordPaneStatus, 'ready'>;
  onClose: () => void;
}

/**
 * The hero's footprint while there is no record to draw: the top row with the
 * close button, then pulsing placeholders shaped like the avatar, name, and
 * amount for `loading`, or just the dark band for the error states.
 */
const RecordHeroStandIn = ({ status, onClose }: IRecordHeroStandInProps) => {
  return (
    <div className="bg-utility-brand-850 flex w-full flex-col items-center pb-10" data-testid={`record-hero-${status}`}>
      <RecordHeroTopRow onClose={onClose} />
      {status === 'loading' && (
        <div className="flex flex-col items-center gap-2 px-5 pb-5" aria-busy="true" aria-label="Loading">
          <div className="bg-alpha-white-10 size-28 animate-pulse rounded-2xl" />
          <div className="bg-alpha-white-10 h-7 w-40 animate-pulse rounded-md" />
          <div className="bg-alpha-white-10 mt-6 h-16 w-56 animate-pulse rounded-md" />
          <div className="mt-6 flex gap-5">
            <div className="bg-alpha-white-10 h-9 w-36 animate-pulse rounded-md" />
            <div className="bg-alpha-white-10 h-9 w-48 animate-pulse rounded-md" />
          </div>
        </div>
      )}
    </div>
  );
};

const RecordBodySkeleton = () => {
  return (
    <div className="flex flex-col gap-5" aria-hidden="true">
      <div className="bg-secondary h-40 w-full animate-pulse rounded-2xl" />
      <div className="bg-secondary h-48 w-full animate-pulse rounded-2xl" />
    </div>
  );
};

export default Object.assign(RecordPane, { Hero: RecordHero, Section: RecordSection, Row: RecordRow });
